/* MissionsHub v2 — Operational Center (Feb 2026 redesign)
   Two-column desktop (feed + live map/detail), mobile single column,
   filter pills (Tutte/Critiche/Vicine/Rifugi/Risolte), card with
   distance/participants/shelter badge/status progression, +Segnala CTA sticky.
*/
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  AlertTriangle, MapPin, Plus, Shield, Heart, Truck, Home as HomeIcon,
  Stethoscope, Search as SearchIcon, Users, Map as MapIcon, Clock,
  ArrowRight, Sparkles, ShieldCheck, ChevronRight, Compass, Activity,
  AlertCircle, X, Send, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet } from "@/components/orio";

/* ------------ Categories and meta ------------ */
const CATEGORIES = {
  injured:    { label: "Cane ferito",        short: "Ferito",       color: "#FF3B30", icon: AlertTriangle, role: "any",
                titleHint: "Es. Cane ferito in via Roma",       descHint: "Taglia, ferite visibili, se si lascia avvicinare, condizioni. Chi arriva sa cosa portare (asciugamano, museruola, trasportino)." },
  stray:      { label: "Cane smarrito",      short: "Smarrito",     color: "#FFB020", icon: SearchIcon,    role: "any",
                titleHint: "Es. Golden 'Luna' smarrito · Bologna centro", descHint: "Razza, colore, età, quando visto l'ultima volta, se ha il chip. Attacca una foto se possibile." },
  sighting:   { label: "Cane avvistato",     short: "Avvistato",    color: "#FFB020", icon: SearchIcon,    role: "any",
                titleHint: "Es. Visto meticcio marrone in Piazza Duomo",  descHint: "Come sta, direzione di marcia, se ha un guinzaglio, se sembra spaventato. Se non è tuo, dai info per il proprietario." },
  abuse:      { label: "Maltrattamento",     short: "Maltratto",    color: "#FF3B30", icon: Shield,        role: "any",
                titleHint: "Es. Cane legato senza acqua da giorni",       descHint: "Indirizzo, da quanto tempo, foto, se ci sono altri testimoni. Verrà passata a ORIO Guardiani." },
  urgent:     { label: "Urgenza",            short: "Urgente",      color: "#FF3B30", icon: AlertTriangle, role: "any",
                titleHint: "Es. Serve un aiuto SUBITO",                  descHint: "Cosa sta succedendo adesso? Descrivi in poche righe la situazione e il tipo di aiuto necessario." },
  transport:  { label: "Staffetta",          short: "Staffetta",    color: "#34D399", icon: Truck,         role: "shelter",
                titleHint: "Es. Staffetta Napoli → Milano · sabato",     descHint: "Da dove a dove, giorno preferito, taglia/i, se serve trasportino, se rimborso benzina." },
  foster:     { label: "Stallo",             short: "Stallo",       color: "#34D399", icon: HomeIcon,      role: "shelter",
                titleHint: "Es. Cerchiamo stallo per meticcio 2 anni",   descHint: "Durata prevista, con o senza altri animali, budget cibo, se serve giardino." },
  adoption:   { label: "Adozione urgente",   short: "Adozione",     color: "#FFB020", icon: Heart,         role: "shelter",
                titleHint: "Es. 'Ombra' cerca famiglia per sempre",      descHint: "Età, taglia, indole, storia breve, se convive con bambini/animali. Non serve chip, verrà consegnato." },
  vet_care:   { label: "Cure veterinarie",   short: "Vet",          color: "#FF3B30", icon: Stethoscope,   role: "shelter",
                titleHint: "Es. Serve visita urgente · frattura zampa",  descHint: "Diagnosi sospetta, urgenza, se accetti donazioni o serve un vet convenzionato in zona." },
  rescue:     { label: "Salvataggio",        short: "Salvataggio",  color: "#FF3B30", icon: Users,         role: "shelter_activist",
                titleHint: "Es. Salvataggio randagio ferito in autostrada", descHint: "Ora del salvataggio, luogo esatto, mezzi necessari, se serve backup." },
  event:      { label: "Evento",             short: "Evento",       color: "#34D399", icon: Sparkles,      role: "any",
                titleHint: "Es. Aperitivo cinofilo · sabato ore 18",     descHint: "Chi può partecipare, cosa portare, se serve prenotazione, se ci sono premi/gadget." },
  fundraising:{ label: "Raccolta fondi",     short: "Fondi",        color: "#FFB020", icon: Heart,         role: "any",
                titleHint: "Es. Aiuto per operazione di 'Rocky'",         descHint: "Obiettivo € totale, storia del cane, quanto già raccolto, link IBAN/paypal se pubblico." },
};

const URGENCY = {
  critical: { label: "Critica", color: "#FF3B30" },
  high:     { label: "Alta",    color: "#FFB020" },
  normal:   { label: "Media",   color: "#34D399" },
};

const PROGRESSION = [
  { id: "reported",   label: "Segnalata" },
  { id: "verifying",  label: "In verifica" },
  { id: "incoming",   label: "Aiuto in arrivo" },
  { id: "on_site",    label: "Presa in carico" },
  { id: "safe",       label: "Cane al sicuro" },
];

function getProgressionStep(m) {
  if (m.status === "closed" && m.outcome === "saved") return 4;
  if (m.status === "closed") return 4;
  if (m.status === "pending_close") return 4;
  const onSite = (m.checkpoints || []).some((c) => c.type === "on_site");
  if (m.status === "in_progress" && onSite) return 3;
  if (m.status === "in_progress") return 2;
  if ((m.participants || []).length > 0) return 2;
  if ((m.checkpoints || []).length > 0) return 1;
  return 0;
}

const FILTERS = [
  { id: "all",      label: "Tutte" },
  { id: "critical", label: "Critiche" },
  { id: "near",     label: "Vicine" },
  { id: "shelter",  label: "Rifugi" },
  { id: "resolved", label: "Risolte" },
];

/* ------------ Helpers ------------ */
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function relative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "Adesso";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}g fa`;
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

/* ------------ Leaflet icons ------------ */
function makePinIcon(color) {
  const html = `
    <span style="position:relative;display:block;width:28px;height:28px;">
      <span style="position:absolute;inset:0;border-radius:9999px;background:${color};opacity:0.35;animation:orio-ping 2s cubic-bezier(0,0,0.2,1) infinite"></span>
      <span style="position:absolute;inset:6px;border-radius:9999px;background:${color};box-shadow:0 0 0 2px rgba(0,0,0,0.55), 0 0 14px ${color}AA"></span>
    </span>`;
  return L.divIcon({ html, className: "", iconSize: [28, 28], iconAnchor: [14, 14] });
}

function makeUserIcon() {
  const html = `
    <span style="position:relative;display:block;width:18px;height:18px;">
      <span style="position:absolute;inset:0;border-radius:9999px;background:#3b82f6;opacity:0.30;animation:orio-ping 2s cubic-bezier(0,0,0.2,1) infinite"></span>
      <span style="position:absolute;inset:4px;border-radius:9999px;background:#3b82f6;box-shadow:0 0 0 2px rgba(0,0,0,0.65)"></span>
    </span>`;
  return L.divIcon({ html, className: "", iconSize: [18, 18], iconAnchor: [9, 9] });
}

/* ------------------------------------------------------------------ */
export default function MissionsHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [missions, setMissions] = useState([]);
  const [shelters, setShelters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(params.get("filter") || "all");
  const [selectedId, setSelectedId] = useState(params.get("id") || null);
  const [createOpen, setCreateOpen] = useState(false);
  const [userPos, setUserPos] = useState(null);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);

  // Load data
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [m, sh] = await Promise.all([api.get("/missions/v2"), api.get("/shelters")]);
      setMissions(m.data.missions || []);
      setShelters(sh.data || []);
    } catch (e) {
      toast.error("Impossibile caricare il centro operativo");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // User position
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8000 }
    );
  }, []);

  // Sync filter state with URL
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (filter === "all") next.delete("filter"); else next.set("filter", filter);
    if (selectedId) next.set("id", selectedId); else next.delete("id");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedId]);

  // Decorate missions with distance + shelter link
  const decorated = useMemo(() => {
    return missions.map((m) => {
      let distance_km = m.distance_km;
      if (distance_km == null && userPos && m.lat && m.lng) {
        distance_km = +haversineKm(userPos.lat, userPos.lng, m.lat, m.lng).toFixed(1);
      }
      const linked_shelter = m.creator_role === "shelter"
        ? { id: m.creator_id, name: m.creator_shelter_name || m.creator_name }
        : null;
      return { ...m, distance_km, linked_shelter };
    });
  }, [missions, userPos]);

  // Stat strip
  const stats = useMemo(() => {
    const active = decorated.filter((m) => m.status !== "closed");
    const participants = new Set();
    active.forEach((m) => (m.participants || []).forEach((p) => participants.add(p.user_id)));
    const shelterLinks = new Set(active.filter((m) => m.linked_shelter).map((m) => m.linked_shelter.id));
    return {
      active: active.length,
      participants: participants.size,
      sheltersConnected: shelterLinks.size,
    };
  }, [decorated]);

  // Filter
  const filtered = useMemo(() => {
    let arr = decorated;
    const maxKm = user?.radius_prefs?.missions ?? 25;
    if (filter === "critical") arr = arr.filter((m) => m.urgency === "critical");
    else if (filter === "near") arr = arr.filter((m) => (m.distance_km ?? 999) <= maxKm);
    else if (filter === "shelter") arr = arr.filter((m) => m.linked_shelter || ["transport","foster","adoption","vet_care","rescue"].includes(m.category));
    else if (filter === "resolved") arr = arr.filter((m) => m.status === "closed");
    else arr = arr.filter((m) => m.status !== "closed" && (m.distance_km == null || m.distance_km <= maxKm * 1.5)); // "Tutte" rispetta un raggio ampio
    // sort: critical first, then by distance, then by recency
    return arr.sort((a, b) => {
      const ua = a.urgency === "critical" ? 0 : a.urgency === "high" ? 1 : 2;
      const ub = b.urgency === "critical" ? 0 : b.urgency === "high" ? 1 : 2;
      if (ua !== ub) return ua - ub;
      const da = a.distance_km ?? 9999;
      const db = b.distance_km ?? 9999;
      if (da !== db) return da - db;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [decorated, filter, user]);

  const selected = useMemo(() => decorated.find((m) => m.id === selectedId) || filtered[0] || null, [decorated, selectedId, filtered]);

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32" data-testid="missions-hub">
      {/* ---------- Header ---------- */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] inline-flex items-center gap-1.5 mb-2">
            <Activity className="h-3 w-3" /> Centro operativo
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
            Missioni <span className="orio-grad-text">vicino a te</span>
          </h1>
          <p className="text-[#6B6B6B] mt-2 text-sm max-w-2xl leading-relaxed">
            Segnala, partecipa e aggiorna le missioni fino a quando il cane è al sicuro. Ogni intervento qui è reale.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="open-create-mission"
            onClick={() => setCreateOpen(true)}
            className="px-4 py-2.5 rounded-full bg-white text-black font-semibold text-sm inline-flex items-center gap-2 hover:bg-white/90 transition-colors"
          >
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Segnala cane</span><span className="sm:hidden">Segnala</span>
          </button>
          <Link
            to="/app/mappa"
            data-testid="open-map-link"
            className="hidden sm:inline-flex px-4 py-2.5 rounded-full bg-[#FAF7F0] hover:bg-[#F4EFE6] border border-[#F0EBE3] text-sm items-center gap-2 transition-colors"
          >
            <MapIcon className="h-4 w-4" /> Apri mappa
          </Link>
        </div>
      </div>

      {/* ---------- Zone summary ---------- */}
      <div className="mt-6 flex items-center gap-4 sm:gap-6 px-4 py-4 rounded-3xl border border-[#F0EBE3] bg-white overflow-x-auto no-scrollbar shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]">
        <ZoneStat color="#E94545" label="Missioni attive" value={stats.active} testid="stat-active" />
        <div className="h-8 w-px bg-[#F0EBE3]" />
        <ZoneStat color="#FF7A1A" label="Persone disponibili" value={stats.participants} testid="stat-participants" />
        <div className="h-8 w-px bg-[#F0EBE3]" />
        <ZoneStat color="#2DBA60" label="Rifugi collegati" value={stats.sheltersConnected} testid="stat-shelters" />
      </div>

      {/* ---------- Filter pills ---------- */}
      <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
        {FILTERS.map((f) => {
          const km = user?.radius_prefs?.missions ?? 25;
          const label = f.id === "near" ? `≤ ${km} km` : f.label;
          return (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                filter === f.id
                  ? "bg-[#FF7A1A] text-white border-[#FF7A1A] shadow-[0_4px_12px_-4px_rgba(255,122,26,0.4)]"
                  : "bg-white text-[#6B6B6B] hover:text-[#1A1A1A] border-[#F0EBE3]"
              }`}
            >
              {label}
            </button>
          );
        })}
        <Link
          to="/app/profilo?tab=pubblico&sub=distanze"
          data-testid="radius-settings-link"
          className="px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border bg-[#FAF7F0] text-[#9A9A9A] hover:text-[#1A1A1A] border-[#F0EBE3] inline-flex items-center gap-1"
        >
          <Compass className="h-3 w-3" /> raggio
        </Link>
      </div>

      {/* ---------- Two-column desktop layout ---------- */}
      <div className="mt-5 lg:grid lg:grid-cols-[1fr_440px] lg:gap-6 xl:grid-cols-[1fr_520px]">
        {/* ----- LEFT: Feed ----- */}
        <div className="space-y-3">
          {loading ? (
            <SkeletonFeed />
          ) : filtered.length === 0 ? (
            <EmptyFeed filter={filter} />
          ) : (
            filtered.map((m) => (
              <MissionCard
                key={m.id}
                m={m}
                isSelected={selected?.id === m.id}
                onSelect={() => setSelectedId(m.id)}
                onOpen={() => navigate(`/app/missioni/${m.id}`)}
                onJoin={async () => {
                  try {
                    await api.post(`/missions/v2/${m.id}/join`, { skills: [], note: "" });
                    toast.success("Ti sei unito alla missione");
                    refresh();
                  } catch (e) {
                    toast.error(formatErr(e.response?.data?.detail));
                  }
                }}
                currentUserId={user?.id}
              />
            ))
          )}
        </div>

        {/* ----- RIGHT (desktop): live map + selected detail ----- */}
        <div className="hidden lg:block">
          <div className="sticky top-20 space-y-3" style={{ maxHeight: "calc(100vh - 6rem)" }}>
            <div className="rounded-3xl overflow-hidden border border-[#F0EBE3]" style={{ height: 320 }}>
              <LiveMap missions={filtered} userPos={userPos} selectedId={selected?.id} onSelect={(id) => setSelectedId(id)} />
            </div>
            {selected && (
              <SelectedMissionPanel
                m={selected}
                currentUserId={user?.id}
                onOpen={() => navigate(`/app/missioni/${selected.id}`)}
                onJoin={async () => {
                  try {
                    await api.post(`/missions/v2/${selected.id}/join`, { skills: [], note: "" });
                    toast.success("Ti sei unito alla missione");
                    refresh();
                  } catch (e) {
                    toast.error(formatErr(e.response?.data?.detail));
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ---------- Mobile "Apri mappa" FAB at bottom-left ---------- */}
      <button
        data-testid="mobile-open-map"
        onClick={() => setMobileMapOpen(true)}
        className="lg:hidden fixed left-4 z-30 px-4 py-3 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] backdrop-blur-md border border-[#F0EBE3] text-sm font-medium inline-flex items-center gap-2 transition-colors"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 88px)" }}
      >
        <MapIcon className="h-4 w-4" /> Mappa zona
      </button>

      {/* Mobile map sheet */}
      <Sheet
        open={mobileMapOpen}
        onClose={() => setMobileMapOpen(false)}
        eyebrow="Mappa zona"
        title="Missioni nella tua area"
        testid="mobile-map-sheet"
      >
        <div className="rounded-2xl overflow-hidden border border-[#F0EBE3]" style={{ height: 380 }}>
          <LiveMap missions={filtered} userPos={userPos} selectedId={selected?.id} onSelect={(id) => { setSelectedId(id); setMobileMapOpen(false); }} />
        </div>
        <p className="text-[11px] text-[#9A9A9A] mt-3">
          Tocca un pin per selezionare la missione. Critiche in rosso, vicine in alto.
        </p>
      </Sheet>

      {/* Shelter inbox link */}
      {user?.is_shelter && (
        <Link
          to="/app/missioni/rifugio"
          data-testid="shelter-mode-link"
          className="mt-8 block rounded-2xl border border-[#FFB020]/30 bg-[#FFB020]/[0.08] hover:bg-[#FFB020]/[0.12] p-4 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,176,32,0.20)", color: "#FFB020" }}>
              <HomeIcon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">Modalità rifugio</div>
              <div className="text-sm font-medium">Apri la dashboard del tuo rifugio</div>
            </div>
            <ChevronRight className="h-4 w-4 text-[#9A9A9A]" />
          </div>
        </Link>
      )}

      {/* Create mission sheet */}
      {createOpen && (
        <CreateMissionSheet
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); refresh(); }}
          role={user?.is_shelter ? "shelter" : "citizen"}
          userPos={userPos}
        />
      )}
    </div>
  );
}

/* ============================================================== */
/* ZoneStat                                                       */
/* ============================================================== */
function ZoneStat({ label, value, color, testid }) {
  return (
    <div className="inline-flex items-center gap-2 shrink-0" data-testid={testid}>
      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
      <div>
        <div className="font-display text-xl leading-none tabular-nums">{value}</div>
        <div className="text-[10px] text-[#9A9A9A] uppercase tracking-wider mt-1">{label}</div>
      </div>
    </div>
  );
}

/* ============================================================== */
/* MissionCard                                                     */
/* ============================================================== */
function MissionCard({ m, isSelected, onSelect, onOpen, onJoin, currentUserId }) {
  const cat = CATEGORIES[m.category] || CATEGORIES.urgent;
  const urg = URGENCY[m.urgency] || URGENCY.normal;
  const Icon = cat.icon;
  const step = getProgressionStep(m);
  const isParticipant = (m.participants || []).some((p) => p.user_id === currentUserId);
  const isCreator = m.creator_id === currentUserId;
  const isClosed = m.status === "closed";

  return (
    <div
      data-testid={`mission-card-${m.id}`}
      className={`group rounded-3xl border overflow-hidden cursor-pointer transition-all bg-white ${
        isSelected ? "border-[#FF7A1A]/40 ring-1 ring-[#FF7A1A]/30 shadow-[0_8px_30px_-8px_rgba(255,122,26,0.20)]" : "border-[#F0EBE3] hover:border-[#D8CFB6] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]"
      }`}
      onClick={onSelect}
    >
      <div className="p-4 sm:p-5">
        {/* meta row */}
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.18em]">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold"
            style={{
              background: `${urg.color}22`,
              color: urg.color,
              border: `1px solid ${urg.color}55`,
            }}
          >
            {isClosed ? "Risolta" : urg.label}
          </span>
          <span className="inline-flex items-center gap-1 text-[#9A9A9A]">
            <Icon className="h-3 w-3" style={{ color: cat.color }} />
            <span>{cat.label}</span>
          </span>
          <span className="text-[#C7C0B3]">·</span>
          <span className="text-[#9A9A9A] inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {relative(m.created_at)}
          </span>
        </div>

        {/* title + description */}
        <div className="mt-2.5 font-display text-lg sm:text-xl leading-snug text-[#1A1A1A]">{m.title}</div>
        {m.description && (
          <p className="text-[13px] text-[#6B6B6B] mt-1.5 line-clamp-2 leading-relaxed">{m.description}</p>
        )}

        {/* facts row */}
        <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#9A9A9A]">
          {m.distance_km != null && (
            <span className="inline-flex items-center gap-1">
              <Compass className="h-3 w-3 text-[#9A9A9A]" /> {m.distance_km} km da te
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3 text-[#9A9A9A]" /> {(m.participants || []).length} partecipant{(m.participants || []).length === 1 ? "e" : "i"}
          </span>
          {m.linked_shelter && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
              style={{ background: "rgba(255,176,32,0.12)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.30)" }}>
              <HomeIcon className="h-3 w-3" /> {m.linked_shelter.name}
            </span>
          )}
        </div>

        {/* progression */}
        <div className="mt-3.5">
          <Progression step={step} accent={cat.color} compact />
        </div>

        {/* actions */}
        {!isClosed && (
          <div className="mt-3.5 flex items-center gap-2">
            {!isParticipant && !isCreator && (
              <button
                data-testid={`mission-join-${m.id}`}
                onClick={(e) => { e.stopPropagation(); onJoin(); }}
                className="orio-cta-btn px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
              >
                <Users className="h-3.5 w-3.5" /> Partecipa
              </button>
            )}
            {(isParticipant || isCreator) && (
              <span className="orio-pill orio-pill-green">
                <ShieldCheck className="h-3 w-3" />
                {isCreator ? "Sei il segnalante" : "Già partecipi"}
              </span>
            )}
            <button
              data-testid={`mission-open-${m.id}`}
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              className="px-3.5 py-2 rounded-full text-xs font-medium text-[#1A1A1A] hover:bg-[#F4EFE6] border border-[#F0EBE3] inline-flex items-center gap-1.5 transition-colors"
            >
              Apri <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
        {isClosed && (
          <div className="mt-3.5 text-[12px] text-[#2DBA60] inline-flex items-center gap-1.5 font-medium">
            <ShieldCheck className="h-3.5 w-3.5" /> Missione risolta · {relative(m.updated_at || m.created_at)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================== */
/* Progression bar                                                 */
/* ============================================================== */
function Progression({ step, accent = "#FFB020", compact = false }) {
  return (
    <div>
      <div className="flex items-center gap-1">
        {PROGRESSION.map((s, i) => {
          const reached = i <= step;
          return (
            <div key={s.id} className="flex-1 relative">
              <div
                className="h-1 rounded-full transition-colors"
                style={{
                  background: reached ? accent : "#F4EFE6",
                  boxShadow: i === step ? `0 0 8px ${accent}` : "none",
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] tracking-wide">
        <span className="text-[#9A9A9A]">{compact ? PROGRESSION[step]?.label : "Stato"}</span>
        {!compact && <span style={{ color: accent }} className="font-medium">{PROGRESSION[step]?.label}</span>}
      </div>
    </div>
  );
}

/* ============================================================== */
/* SelectedMissionPanel (desktop right column)                     */
/* ============================================================== */
function SelectedMissionPanel({ m, currentUserId, onOpen, onJoin }) {
  const cat = CATEGORIES[m.category] || CATEGORIES.urgent;
  const urg = URGENCY[m.urgency] || URGENCY.normal;
  const Icon = cat.icon;
  const step = getProgressionStep(m);
  const isParticipant = (m.participants || []).some((p) => p.user_id === currentUserId);
  const isCreator = m.creator_id === currentUserId;
  const isCritical = m.urgency === "critical" && m.status !== "closed";

  return (
    <div className="rounded-3xl border border-[#F0EBE3] overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(28,28,30,0.65) 0%, rgba(12,12,14,0.78) 100%)",
        backdropFilter: "blur(24px) saturate(160%)",
      }}>
      <div className="p-5">
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.18em]">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold"
            style={{ background: `${urg.color}22`, color: urg.color, border: `1px solid ${urg.color}55` }}
          >
            {urg.label}
          </span>
          <span className="inline-flex items-center gap-1 text-[#9A9A9A]">
            <Icon className="h-3 w-3" style={{ color: cat.color }} /> {cat.label}
          </span>
          <span className="text-[#C7C0B3]">·</span>
          <span className="text-[#9A9A9A] inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {relative(m.created_at)}
          </span>
        </div>
        <div className="mt-2 font-display text-xl leading-snug">{m.title}</div>
        {m.description && (
          <p className="text-[12px] text-[#6B6B6B] mt-1.5 line-clamp-3 leading-relaxed">{m.description}</p>
        )}

        <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#9A9A9A]">
          {m.distance_km != null && (
            <span className="inline-flex items-center gap-1">
              <Compass className="h-3 w-3 text-[#9A9A9A]" /> {m.distance_km} km
            </span>
          )}
          {m.location_name && (
            <span className="inline-flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3 text-[#9A9A9A]" /> {m.location_name}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3 text-[#9A9A9A]" /> {(m.participants || []).length}
          </span>
        </div>

        {/* Safety box for critical */}
        {isCritical && (
          <div className="mt-4 rounded-2xl border p-3 flex items-start gap-2"
            style={{ background: "rgba(255,59,48,0.10)", borderColor: "rgba(255,59,48,0.35)" }}>
            <AlertCircle className="h-4 w-4 text-[#FF3B30] shrink-0 mt-0.5" />
            <div className="text-[11px] text-[#1A1A1A] leading-relaxed">
              Non avvicinarti se il cane è spaventato o aggressivo. Mantieni distanza, aggiorna la posizione e contatta un rifugio o un professionista.
            </div>
          </div>
        )}

        {/* Progression */}
        <div className="mt-4">
          <Progression step={step} accent={cat.color} />
        </div>

        {/* Participants avatars */}
        {(m.participants || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-2">Squadra</div>
            <div className="flex items-center -space-x-2">
              {(m.participants || []).slice(0, 6).map((p) => (
                <div
                  key={p.user_id}
                  title={p.name}
                  className="h-8 w-8 rounded-full border-2 border-[#0a0a0a] bg-[#F0EBE3] flex items-center justify-center text-[11px] font-medium"
                  style={{ background: `hsl(${(p.name?.charCodeAt(0) || 50) * 7 % 360} 50% 25%)` }}
                >
                  {p.name?.[0]?.toUpperCase() || "?"}
                </div>
              ))}
              {(m.participants || []).length > 6 && (
                <div className="h-8 w-8 rounded-full border-2 border-[#0a0a0a] bg-[#F4EFE6] flex items-center justify-center text-[10px] text-[#6B6B6B]">
                  +{(m.participants || []).length - 6}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Linked shelter */}
        {m.linked_shelter && (
          <Link
            to={`/app/rifugi/${m.linked_shelter.id}`}
            data-testid="selected-shelter-link"
            className="mt-4 flex items-center gap-2.5 rounded-xl border border-[#FFB020]/25 bg-[#FFB020]/[0.06] hover:bg-[#FFB020]/[0.10] p-2.5 transition-colors"
          >
            <div className="h-8 w-8 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(255,176,32,0.18)", color: "#FFB020" }}>
              <HomeIcon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">Rifugio collegato</div>
              <div className="text-[13px] truncate">{m.linked_shelter.name}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-[#9A9A9A]" />
          </Link>
        )}

        {/* CTAs */}
        <div className="mt-5 flex items-center gap-2">
          {!isParticipant && !isCreator && m.status !== "closed" && (
            <button
              data-testid="selected-join-btn"
              onClick={onJoin}
              className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(120deg, #ff3b30 0%, #ffb020 50%, #34d399 100%)",
                color: "#000",
              }}
            >
              <Users className="h-4 w-4" /> Partecipa
            </button>
          )}
          <button
            data-testid="selected-open-btn"
            onClick={onOpen}
            className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium bg-[#FAF7F0] hover:bg-[#F4EFE6] border border-[#F0EBE3] inline-flex items-center justify-center gap-2 transition-colors"
          >
            Apri missione <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================== */
/* Live map                                                        */
/* ============================================================== */
function FitToMissions({ missions, userPos }) {
  const map = useMap();
  useEffect(() => {
    const pts = [];
    missions.forEach((m) => m.lat && m.lng && pts.push([m.lat, m.lng]));
    if (userPos) pts.push([userPos.lat, userPos.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], 13);
    } else {
      map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
    }
  }, [missions, userPos, map]);
  return null;
}

function LiveMap({ missions, userPos, selectedId, onSelect }) {
  const initialCenter = userPos
    ? [userPos.lat, userPos.lng]
    : missions[0] && missions[0].lat
      ? [missions[0].lat, missions[0].lng]
      : [41.9028, 12.4964]; // Rome fallback

  return (
    <MapContainer
      center={initialCenter}
      zoom={12}
      scrollWheelZoom
      style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
    >
      <TileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; OpenStreetMap contributors'
        maxZoom={19}
        keepBuffer={6}
        updateWhenIdle={false}
        updateWhenZooming={false}
      />
      <FitToMissions missions={missions} userPos={userPos} />
      {userPos && (
        <Marker position={[userPos.lat, userPos.lng]} icon={makeUserIcon()}>
          <Popup>Tu sei qui</Popup>
        </Marker>
      )}
      {missions.filter((m) => m.lat && m.lng).map((m) => {
        const cat = CATEGORIES[m.category] || CATEGORIES.urgent;
        const urg = URGENCY[m.urgency] || URGENCY.normal;
        const color = m.status === "closed" ? "#34D399" : urg.color;
        return (
          <Marker
            key={m.id}
            position={[m.lat, m.lng]}
            icon={makePinIcon(color)}
            eventHandlers={{ click: () => onSelect(m.id) }}
          >
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color }}>
                  {urg.label} · {cat.short}
                </div>
                <div style={{ marginTop: 4, fontWeight: 600, fontSize: 13 }}>{m.title}</div>
                {m.distance_km != null && (
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{m.distance_km} km</div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}

/* ============================================================== */
/* Skeleton + Empty                                                */
/* ============================================================== */
function SkeletonFeed() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl border border-[#F0EBE3] p-5"
          style={{ background: "rgba(20,20,22,0.5)" }}>
          <div className="h-3 w-1/3 bg-[#F4EFE6] rounded animate-pulse" />
          <div className="h-5 w-3/4 bg-[#F4EFE6] rounded mt-3 animate-pulse" />
          <div className="h-3 w-2/3 bg-[#F4EFE6] rounded mt-2 animate-pulse" />
          <div className="h-1.5 w-full bg-[#F4EFE6] rounded mt-4 animate-pulse" />
        </div>
      ))}
    </>
  );
}

function EmptyFeed({ filter }) {
  const COPY = {
    all: { t: "Nessuna missione aperta nella tua zona.", b: "Stiamo monitorando. Apri tu una segnalazione se vedi un cane in difficoltà." },
    critical: { t: "Nessuna missione critica al momento.", b: "Sono quelle a cui rispondiamo entro 15 minuti." },
    near: { t: "Nessuna missione vicina nel raggio impostato.", b: "Allarga il raggio da Profilo → Distanze, o cambia filtro." },
    shelter: { t: "Nessuna missione legata a rifugi.", b: "Quando un rifugio apre una richiesta apparirà qui." },
    resolved: { t: "Nessuna missione risolta ancora.", b: "Le chiusure verificate appariranno qui con esito." },
  };
  const c = COPY[filter] || COPY.all;
  return (
    <div className="rounded-3xl border border-[#F0EBE3] p-10 sm:p-14 text-center"
      style={{ background: "linear-gradient(180deg, rgba(24,24,26,0.55) 0%, rgba(12,12,14,0.65) 100%)", backdropFilter: "blur(20px)" }}>
      <div className="h-12 w-12 rounded-2xl bg-[#F4EFE6] border border-[#F0EBE3] flex items-center justify-center mx-auto mb-3">
        <Activity className="h-5 w-5 text-[#9A9A9A]" />
      </div>
      <div className="font-display text-xl">{c.t}</div>
      <p className="text-[#9A9A9A] text-sm mt-1.5 max-w-md mx-auto">{c.b}</p>
    </div>
  );
}

/* ============================================================== */
/* CreateMissionSheet                                              */
/* ============================================================== */
export function CreateMissionSheet({ onClose, onDone, role, userPos }) {
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState(userPos || null);
  const [photo, setPhoto] = useState(null);
  const [fundraiseUrl, setFundraiseUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [nearby, setNearby] = useState([]); // online + visible helpers around the mission location

  const activeCat = category ? CATEGORIES[category] : null;
  const isFundraise = category === "fundraising";

  // Fetch nearby helpers when a location is known
  useEffect(() => {
    if (!location) { setNearby([]); return; }
    let cancelled = false;
    api.get("/missioni/visible-users", {
      params: { lat: location.lat, lng: location.lng, radius_km: 15 },
    }).then((r) => {
      if (!cancelled) setNearby(r.data?.items || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [location?.lat, location?.lng]);

  const allowed = Object.entries(CATEGORIES).filter(
    ([, v]) => v.role === "any" || v.role === role || v.role.includes(role)
  );

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS non disponibile");
    navigator.geolocation.getCurrentPosition(
      (p) => setLocation({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => toast.error("Impossibile leggere la posizione"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("Foto troppo grande (max 25 MB)");
    try {
      const { compressImage } = await import("@/lib/imageCompress");
      const dataUrl = await compressImage(f, { maxSide: 1280, quality: 0.85 });
      setPhoto(dataUrl);
    } catch {
      toast.error("Impossibile leggere la foto");
    }
  };

  const submit = async () => {
    if (!category) return toast.error("Scegli il tipo di missione");
    if (!title.trim()) return toast.error("Aggiungi un titolo");
    if (!location) return toast.error("Aggiungi la posizione");
    if (isFundraise && !fundraiseUrl.trim()) {
      return toast.error("Aggiungi il link PayPal.me per la raccolta fondi");
    }
    setLoading(true);
    try {
      await api.post("/missions/v2", {
        category, urgency, title: title.trim(), description: description.trim(),
        lat: location.lat, lng: location.lng, photo_url: photo,
        fundraise_url: fundraiseUrl.trim() || null,
      });
      toast.success("Missione pubblicata");
      onDone();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      eyebrow={activeCat ? "Nuova · " + activeCat.short : "Nuova segnalazione"}
      title={activeCat ? activeCat.label : "Cosa vuoi segnalare?"}
      testid="create-mission-sheet"
      footer={
        <button
          onClick={submit}
          disabled={loading || !category || !title.trim() || !location}
          data-testid="m-submit"
          className="relative w-full px-4 py-3 rounded-full orio-cta-btn font-semibold text-black disabled:opacity-50 overflow-hidden disabled:saturate-50"
        >
          <span className="relative z-10 inline-flex items-center justify-center gap-2">
            <Send className="h-4 w-4" /> {loading ? "Pubblicazione…" : (
              <>
                <span className="sm:hidden">Pubblica</span>
                <span className="hidden sm:inline">Pubblica missione</span>
              </>
            )}
          </span>
        </button>
      }
    >
      {!activeCat ? (
        <p className="text-[12.5px] text-[#6B6B6B] leading-relaxed">
          Scegli il tipo di segnalazione. ORIO ti guiderà passo passo e ti mostrerà chi c'è vicino a te per aiutare.
        </p>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#FAF7F0] border border-[#F0EBE3] w-fit">
          <activeCat.icon className="h-3.5 w-3.5" style={{ color: activeCat.color }} />
          <span className="text-[11.5px] font-semibold" style={{ color: activeCat.color }}>{activeCat.short}</span>
          <button
            type="button"
            onClick={() => { setCategory(""); setTitle(""); setDescription(""); }}
            className="ml-1 text-[10px] text-[#9A9A9A] hover:text-[#1A1A1A]"
            data-testid="m-change-cat"
          >
            cambia
          </button>
        </div>
      )}

      {/* Category picker — visible until a category is selected */}
      {!activeCat && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5">Tipo</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {allowed.map(([key, c]) => {
              const Icon = c.icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setCategory(key);
                    // Auto-suggest title placeholder — user can still edit freely
                    if (!title) setTitle("");
                  }}
                  data-testid={`cat-${key}`}
                  className="rounded-xl p-3 text-left transition-all bg-[#FAF7F0] hover:bg-[#F4EFE6] border border-[#EAE3D2]"
                >
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center mb-2"
                       style={{ background: `${c.color}1A`, color: c.color }}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="text-[12px] font-semibold leading-tight text-[#1A1A1A]">{c.short}</div>
                  <div className="text-[10px] text-[#9A9A9A] mt-0.5 leading-tight line-clamp-1">{c.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Once a category is selected, show dynamic form */}
      {activeCat && (
        <>
          {/* Urgency */}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5">Urgenza</div>
            <div className="inline-flex p-1 rounded-full border border-[#F0EBE3] bg-[#FAF7F0]">
              {Object.entries(URGENCY).reverse().map(([k, v]) => (
                <button
                  key={k}
                  data-testid={`urg-${k}`}
                  onClick={() => setUrgency(k)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors"
                  style={{
                    background: urgency === k ? `${v.color}22` : "transparent",
                    color: urgency === k ? v.color : "#5C5C5C",
                    border: urgency === k ? `1px solid ${v.color}55` : "1px solid transparent",
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title with DYNAMIC placeholder based on category */}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5">Titolo</div>
            <input
              data-testid="m-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder={activeCat.titleHint}
              className="w-full px-3.5 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]/40 text-sm text-[#1A1A1A] placeholder:text-[#B5AE9E] transition-colors"
            />
          </div>

          {/* Description with DYNAMIC placeholder */}
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5">Descrizione</div>
            <textarea
              data-testid="m-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              placeholder={activeCat.descHint}
              className="w-full px-3.5 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]/40 text-sm text-[#1A1A1A] placeholder:text-[#B5AE9E] min-h-[110px] transition-colors"
            />
          </div>

          {/* Photo + Location */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="cursor-pointer px-3 py-3 rounded-xl bg-[#FAF7F0] hover:bg-[#F4EFE6] border border-[#F0EBE3] text-xs inline-flex items-center justify-center gap-1.5 transition-colors">
              <Camera className="h-3.5 w-3.5" /> {photo ? "Foto pronta" : "Aggiungi foto"}
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="m-photo" />
            </label>
            <button
              type="button"
              onClick={useMyLocation}
              data-testid="m-use-loc"
              className="px-3 py-3 rounded-xl bg-[#FAF7F0] hover:bg-[#F4EFE6] border border-[#F0EBE3] text-xs inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <MapPin className="h-3.5 w-3.5" /> {location ? `${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}` : "Mia posizione"}
            </button>
          </div>

          {/* Fundraise link — required for the "fundraising" category,
              optional for any other category. ORIO validates it's a
              paypal.me link, never touches the money. */}
          {(isFundraise || fundraiseUrl) && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] mb-1.5 inline-flex items-center gap-1.5">
                💛 Link raccolta fondi {isFundraise && <span className="text-[#FF7A1A]">*</span>}
              </div>
              <input
                data-testid="m-fundraise"
                value={fundraiseUrl}
                onChange={(e) => setFundraiseUrl(e.target.value)}
                placeholder="https://paypal.me/iltuonome"
                className="w-full px-3.5 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]/40 text-sm transition-colors"
              />
              <div className="text-[11px] text-[#9A9A9A] mt-1.5 leading-snug">
                Le donazioni vanno direttamente al tuo PayPal. ORIO verifica il creatore ma non trattiene commissioni.
              </div>
            </div>
          )}
          {!isFundraise && !fundraiseUrl && (
            <button
              type="button"
              onClick={() => setFundraiseUrl(" ")}
              className="mt-2 text-[12px] text-[#C25612] font-semibold inline-flex items-center gap-1 underline underline-offset-2"
              data-testid="m-add-fundraise"
            >
              💛 Aggiungi link raccolta fondi (opzionale)
            </button>
          )}

          {/* Nearby helpers preview — dynamic, shows online+visible users in radius */}
          {location && (
            <div className="mt-5" data-testid="nearby-helpers-preview">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A]">
                  Chi è vicino ora
                </div>
                <div className="text-[10px] font-semibold text-[#1E7E3F]">
                  {nearby.length > 0 ? `${nearby.length} online entro 15 km` : "in ascolto…"}
                </div>
              </div>
              {nearby.length === 0 ? (
                <div className="rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] px-3 py-3 text-[12px] text-[#6B6B6B] leading-snug">
                  Nessun soccorritore visibile in questo momento. La missione verrà comunque inviata a chi si connette entro 15 km.
                </div>
              ) : (
                <div className="flex items-center gap-2 overflow-x-auto orio-h-scroll pb-1">
                  {nearby.slice(0, 6).map((h) => (
                    <div key={h.id} className="shrink-0 flex flex-col items-center gap-1 w-[52px]" data-testid={`nearby-user-${h.id}`}>
                      <div className="relative h-11 w-11 rounded-full overflow-hidden border-2 border-white shrink-0"
                           style={{ boxShadow: "0 6px 14px -6px rgba(45,186,96,0.55)" }}>
                        {h.photo_url ? (
                          <img src={h.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#34D399] to-[#2DBA60] text-white text-[12px] font-bold">
                            {(h.name || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#2DBA60] border-2 border-white" />
                      </div>
                      <div className="text-[9.5px] text-[#6B6B6B] leading-tight text-center truncate w-full">
                        {h.distance_km} km
                      </div>
                    </div>
                  ))}
                  {nearby.length > 6 && (
                    <div className="shrink-0 h-11 w-11 rounded-full bg-[#F4EFE6] flex items-center justify-center text-[11px] font-bold text-[#6B6B6B]">
                      +{nearby.length - 6}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
